// apps/share-web/src/text-share/words-status.ts
//
// Три состояния текстовой ссылки, в которых слов нет. Каждое — своя страница.
//
// ПОЧЕМУ 410 И 404 РАЗНЫЕ. Решение Сергея от 28.08, дословно из плана:
// «страница „сессия завершена“ с кнопкой на лендинг. Технически 410, а не
// редирект: музыкант в зале должен понять, что ссылка была правильная и
// истекла, а не что он ошибся ссылкой». Один и тот же экран на два ответа стёр
// бы ровно то различие, ради которого воркер эти статусы и разводит (см. шапку
// `functions/api/text-shares/[id].ts`: «404 — no such link. 410 — the link was
// right and its session is over»).
//
// Блок собирает общий `buildEmptyState` (ui/empty-state.ts) — тот же, которым
// маршрут `/setlist/:id` рисует свои «пусто» и «не найдено». Своей вёрстки здесь
// нет намеренно: третий вариант центрированного блока с иконкой разъехался бы с
// первыми двумя первой же правкой. Стили `.es-*` приходят из library.css,
// который тянет main.ts (там же, где и все остальные таблицы страницы).

import { buildEmptyState } from '../ui/empty-state.js';
import { t } from '../ui/i18n.js';

export interface WordsStatusDeps {
  /** Кнопка «что это вообще» — единственный выход со страницы без логина. */
  onLanding: () => void;
}

export interface WordsErrorDeps {
  onRetry: () => void;
}

/** Хост чистится, а не дополняется: экранов на странице ровно один. */
function render(host: HTMLElement, node: HTMLElement): void {
  host.textContent = '';
  host.appendChild(node);
}

/** 410 — ссылка была ПРАВИЛЬНАЯ, и её сессию завершили в десктопе. */
export function renderTextShareEnded(host: HTMLElement, deps: WordsStatusDeps): void {
  render(host, buildEmptyState({
    // Перечёркнутая нота: пели — и отпели. Та же иконка, которой библиотека
    // говорит «здесь музыки нет».
    icon: 'musicOff',
    title: t('textShare.endedTitle'),
    body: t('textShare.endedBody'),
    dataRole: 'text-share-ended',
    action: { label: t('textShare.landingCta'), onClick: () => deps.onLanding() },
  }));
}

/** 404 — такой ссылки не было вовсе (или её нагрузка потеряна). */
export function renderTextShareNotFound(host: HTMLElement, deps: WordsStatusDeps): void {
  render(host, buildEmptyState({
    // Другая иконка, другой заголовок, другой текст — читатель обязан отличить
    // «истекло» от «не туда».
    icon: 'alert',
    title: t('textShare.notFoundTitle'),
    body: t('textShare.notFoundBody'),
    dataRole: 'text-share-not-found',
    action: { label: t('textShare.landingCta'), onClick: () => deps.onLanding() },
  }));
}

/**
 * Ни то, ни другое: сеть или сервер. Слова могут появиться со второй попытки,
 * поэтому здесь кнопка «ещё раз», а не выход на лендинг. Ключи — общие с
 * error-screen плеера (`errorScreen.*`): «не загрузилось» на двух поверхностях
 * одного приложения не должно звучать по-разному.
 */
export function renderTextShareError(host: HTMLElement, deps: WordsErrorDeps): void {
  render(host, buildEmptyState({
    icon: 'alert',
    title: t('errorScreen.loadFailedTitle'),
    body: t('errorScreen.loadFailedBody'),
    dataRole: 'text-share-error',
    action: { label: t('errorScreen.retryBtn'), onClick: () => deps.onRetry() },
  }));
}

/**
 * Нагрузка есть, а песен в ней нет. Это НЕ ошибка загрузки: ссылка живая,
 * читать нечего — владелец поделился пустым сетлистом. Ключ общий с
 * `/setlist/:id` (`setlist.empty`): «сет-лист пуст» на двух поверхностях одного
 * приложения не должно звучать по-разному.
 */
export function renderWordsEmpty(host: HTMLElement): void {
  render(host, buildEmptyState({
    icon: 'musicOff',
    title: t('setlist.empty'),
    body: t('setlist.emptyBody'),
    dataRole: 'text-share-empty',
  }));
}
